import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const KEY = "tessa.order.v1";
const CartContext = createContext(null);

// Session-only order-request builder (sessionStorage). Lines reference
// catalog variety ids; the unit is set to "__pending__" until Catalog units
// are confirmed by the user (see Task 01 handoff).
export function CartProvider({ children }) {
  const [lines, setLines] = useState([]);

  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(KEY);
      if (raw) setLines(JSON.parse(raw));
    } catch (_) {}
  }, []);

  const persist = useCallback((next) => {
    setLines(next);
    try { window.sessionStorage.setItem(KEY, JSON.stringify(next)); } catch (_) {}
  }, []);

  const addLine = useCallback((variety) => {
    setLines((prev) => {
      const existing = prev.find((l) => l.variety_id === variety.id);
      let next;
      if (existing) {
        next = prev.map((l) =>
          l.variety_id === variety.id ? { ...l, quantity: l.quantity + 25 } : l
        );
      } else {
        next = [...prev, {
          variety_id: variety.id,
          variety_name: variety.name,
          image: variety.image,
          quantity: 25,
          unit: "__pending__",
        }];
      }
      try { window.sessionStorage.setItem(KEY, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, []);

  const updateLine = useCallback((variety_id, patch) => {
    setLines((prev) => {
      const next = prev.map((l) => l.variety_id === variety_id ? { ...l, ...patch } : l);
      try { window.sessionStorage.setItem(KEY, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, []);

  const removeLine = useCallback((variety_id) => {
    setLines((prev) => {
      const next = prev.filter((l) => l.variety_id !== variety_id);
      try { window.sessionStorage.setItem(KEY, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, []);

  const clear = useCallback(() => persist([]), [persist]);

  const count = lines.length;

  return (
    <CartContext.Provider value={{ lines, addLine, updateLine, removeLine, clear, count }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
